import { canvasUrl } from "./api.js";
import { gradientVector } from "./layerStyle.jsx";

// Recolouring an uploaded SVG. The upload stays as it is: `edits` maps each of
// its original colours (#rrggbb) to a colour, "none", or a gradient spec, and
// recolorSvg builds the finished file as a data URL, which is what the layer
// shows and the server draws.

const PAINT_PROPS = ["fill", "stroke", "stop-color"];
const CSS_PAINT = /\b(fill|stroke|stop-color)\s*:\s*([^;}"']+)/gi;
const SHAPE_TAGS = new Set(["path", "rect", "circle", "ellipse", "polygon", "text"]);

export const isSvgUrl = (url) => /\.svg($|\?)/i.test(url || "");

const texts = new Map();
export function loadSvgText(url) {
  if (!texts.has(url)) {
    const request = fetch(canvasUrl(url)).then((r) => {
      if (!r.ok) throw new Error(`Could not read ${url}`);
      return r.text();
    });
    request.catch(() => texts.delete(url));
    texts.set(url, request);
  }
  return texts.get(url);
}

const probe = document.createElement("canvas").getContext("2d");

// Any CSS colour as #rrggbb; null for none, url(...), inherit and invalid values.
function toHex(value) {
  const v = (value || "").trim().toLowerCase();
  if (!v || ["none", "transparent", "inherit"].includes(v) || v.startsWith("url(")) return null;
  if (v === "currentcolor") return "#000000";
  probe.fillStyle = "#000000";
  probe.fillStyle = v;
  const onBlack = probe.fillStyle;
  probe.fillStyle = "#ffffff";
  probe.fillStyle = v;
  if (onBlack !== probe.fillStyle) return null;
  if (onBlack.startsWith("#")) return onBlack;
  const [r, g, b] = onBlack.match(/[\d.]+/g).map(Number);
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
}

function parse(text) {
  return new DOMParser().parseFromString(text, "image/svg+xml");
}

const paintsIn = (el) =>
  el.hasAttribute("fill") || /(^|[;\s])fill\s*:/.test(el.getAttribute("style") || "");

// Shapes with no fill anywhere draw black, with no colour written in the file.
function hasImplicitBlack(doc) {
  const classStyled = Boolean(doc.querySelector("style"));
  return Array.from(doc.querySelectorAll("*")).some((el) => {
    if (!SHAPE_TAGS.has(el.localName) || el.closest("defs")) return false;
    if (classStyled && el.hasAttribute("class")) return false;
    for (let node = el; node && node.nodeType === 1; node = node.parentNode) {
      if (paintsIn(node)) return false;
    }
    return true;
  });
}

// Calls `paint(hex, prop)` for every colour; a returned string replaces it.
function eachPaint(doc, paint) {
  const swap = (raw, prop) => {
    const hex = toHex(raw);
    return hex ? paint(hex, prop) : undefined;
  };
  doc.querySelectorAll("*").forEach((el) => {
    PAINT_PROPS.forEach((prop) => {
      if (!el.hasAttribute(prop)) return;
      const next = swap(el.getAttribute(prop), prop);
      if (next != null) el.setAttribute(prop, next);
    });
    const style = el.getAttribute("style");
    if (style) {
      el.setAttribute("style", style.replace(CSS_PAINT, (all, prop, raw) => {
        const next = swap(raw, prop.toLowerCase());
        return next != null ? `${prop}:${next}` : all;
      }));
    }
    if (el.localName === "style") {
      el.textContent = el.textContent.replace(CSS_PAINT, (all, prop, raw) => {
        const next = swap(raw, prop.toLowerCase());
        return next != null ? `${prop}:${next}` : all;
      });
    }
  });
}

/** The file's colours, most used first. */
export function svgColors(text) {
  const doc = parse(text);
  const counts = new Map();
  eachPaint(doc, (hex) => {
    counts.set(hex, (counts.get(hex) || 0) + 1);
  });
  if (hasImplicitBlack(doc)) counts.set("#000000", (counts.get("#000000") || 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([hex]) => hex);
}

function addGradient(doc, id, spec) {
  const NS = "http://www.w3.org/2000/svg";
  const root = doc.documentElement;
  let defs = root.querySelector(":scope > defs");
  if (!defs) {
    defs = doc.createElementNS(NS, "defs");
    root.insertBefore(defs, root.firstChild);
  }
  const radial = spec.kind === "radial";
  const grad = doc.createElementNS(NS, radial ? "radialGradient" : "linearGradient");
  grad.setAttribute("id", id);
  if (radial) {
    Object.entries({ cx: 0.5, cy: 0.5, r: 0.5 }).forEach(([k, v]) => grad.setAttribute(k, v));
  } else {
    const { x1, y1, x2, y2 } = gradientVector(spec.angle);
    Object.entries({ x1, y1, x2, y2 }).forEach(([k, v]) => grad.setAttribute(k, v));
  }
  [[0, spec.color_start], [1, spec.color_end]].forEach(([offset, color]) => {
    const stop = doc.createElementNS(NS, "stop");
    stop.setAttribute("offset", offset);
    stop.setAttribute("stop-color", color);
    grad.appendChild(stop);
  });
  defs.appendChild(grad);
}

/** The recoloured file, as a data URL. */
export function recolorSvg(text, edits) {
  const doc = parse(text);
  const implicit = hasImplicitBlack(doc);
  const replacement = (hex, prop) => {
    const edit = edits[hex];
    if (edit == null) return undefined;
    if (typeof edit === "string") return edit;
    // A gradient stop can only hold a plain colour.
    return prop === "stop-color" ? edit.color_start : `url(#mas-${hex.slice(1)})`;
  };
  Object.entries(edits).forEach(([hex, edit]) => {
    if (typeof edit !== "string") addGradient(doc, `mas-${hex.slice(1)}`, edit);
  });
  eachPaint(doc, replacement);
  if (implicit && edits["#000000"] != null) {
    doc.documentElement.setAttribute("fill", replacement("#000000", "fill"));
  }
  const bytes = new TextEncoder().encode(new XMLSerializer().serializeToString(doc));
  const binary = bytes.reduce((out, byte) => out + String.fromCharCode(byte), "");
  return `data:image/svg+xml;base64,${btoa(binary)}`;
}
